import AdConfigCRUDActions from '@mm/state/actions/AdConfigCRUDActions';
import AdConfigModel from '@mm/state/model/AdConfigModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { selectMessengerAds } from '@mm/state/selectors/adConfigSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { isEmpty } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import {
  DEFAULT_NAME,
  PUBLISH_AD_TOP,
  SAVE_AD,
} from '../../../constants/constants';
import { onClickActivate, onClickSave } from '../../../utils/utils';
import { EditHeader } from '../../EditHeader/EditHeader';
import { useInactiveBotActivator } from '../../../../../common/hooks/use-inactive-bot-activator';
import { useBotChannels } from '../../../../../../common/hooks/use-bot-channels';
import classNames from 'classnames';

type Props = {
  adConfig: AdConfigModel | null;
  onSaveName?: (name: string) => void;
  canPublish: boolean;
  updates: Partial<AdConfigModel>;
  updated: boolean;
  errors?: string[];
  isTemplateBot: boolean;
  showOnlyActionButton?: boolean;
  actionButtonClassName?: string;
  usedInSlideout?: boolean;
  onActivateSuccess?: () => void;
};

export const EditAdConfigHeader = React.memo((props: Props) => {
  const {
    adConfig,
    onSaveName,
    canPublish,
    updates,
    errors,
    updated,
    isTemplateBot,
    showOnlyActionButton,
    actionButtonClassName,
    usedInSlideout,
    onActivateSuccess,
  } = props;
  const valid = isEmpty(errors);
  const dispatch: Dispatch<StoreState> = useDispatch();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { history } = useRouter();
  const { activateBotGuard } = useInactiveBotActivator();
  const { activateChannelGuard } = useBotChannels();

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const messengerAds = useSelector(selectMessengerAds).filter(a => !a.draft);
  const pro = !!featureFlags.adsOneNewCtmAd;
  const armLandmine =
    !pro ||
    (!featureFlags.adsUnlimitedNewCtmAds && (messengerAds?.length || 0) > 1);

  let saveText;
  let onSave = () => {};
  let saveDisabled;
  let saveButtonClass;
  if (!adConfig || adConfig.draft) {
    saveText = PUBLISH_AD_TOP;
    saveButtonClass = classNames(
      'edit-header__activate-button',
      actionButtonClassName,
    );
    saveDisabled = !adConfig || !canPublish || isTemplateBot;
    onSave = () =>
      onClickActivate(
        dispatch,
        history,
        adConfig,
        chatbotId,
        armLandmine,
        pro,
        usedInSlideout,
        onActivateSuccess,
      );
  } else {
    saveText = SAVE_AD;
    saveDisabled = (isEmpty(updates) && !updated) || !canPublish;
    saveButtonClass = classNames('edit-header__save-button', {
      'ad-detail__slideout-header-action-button': usedInSlideout,
    });
    onSave = () =>
      onClickSave(
        dispatch,
        history,
        adConfig.id,
        chatbotId,
        updates,
        armLandmine,
        pro,
        usedInSlideout,
        onActivateSuccess,
      );
  }

  const onClickDelete = () => {
    if (!adConfig) {
      return;
    }
    const deleteCallback = async () => {
      await dispatch(
        AdConfigCRUDActions.delete(chatbotId, adConfig as AdConfigModel),
      );

      const betaPathname = BotBuilderRoutes.campaigns.getLink({
        chatbotId,
        kind: 'ctm_ads',
      });
      if (betaPathname) {
        history.push(betaPathname);
        return;
      }
    };

    dispatch(
      modalActions.openDeleteConfirmModal({
        headerText: `Are you sure you want to delete this ${
          adConfig.draft ? 'ad draft' : 'ad'
        }?`,
        deleteCallback,
      }),
    );
  };

  const onSaveClick = () => {
    activateBotGuard()
      .then(() => activateChannelGuard(['facebook']))
      .then(onSave)
      .catch(() => {}); // handled in the guard
  };

  return (
    <EditHeader
      defaultName={DEFAULT_NAME}
      valid={valid}
      entity={adConfig}
      errors={errors}
      saveButtonClass={saveButtonClass}
      saveButtonText={saveText}
      onClickDelete={onClickDelete}
      onSaveName={onSaveName}
      onSaveClick={onSaveClick}
      saveDisabled={saveDisabled}
      showOnlyActionButton={showOnlyActionButton}
    />
  );
});
